import { useCallback, useMemo } from "react";
import map from "ramda/src/map";
import uniq from "ramda/src/uniq";
import prop from "ramda/src/prop";
import pathOr from "ramda/src/pathOr";
import uniqBy from "ramda/src/uniqBy";
import propEq from "ramda/src/propEq";
import compose from "ramda/src/compose";
import andThen from "ramda/src/andThen";
import flatten from "ramda/src/flatten";

import { normalize } from "../helpers/normalize";
import Api from "../services/Api";
import {
  GetAllNetworkInfo,
  IExtendedL3,
  Network,
} from "../services/NetworkApi";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../contexts/NetworkContext/context";
import useAuth from "../routes/login/AuthContext";

const DEFAULT_NETWORK: Network = {
  apt: false,
  coedEnabled: false,
  placementWorksheetEnabled: false,
  matchingStartDate: "",
  matchingEndDate: "",
  activationEndDate: "",
  activationStartDate: "",
  entrataReminderEmails: null,
  genderEnabled: false,
  enabled: false,
  bioEnabled: false,
  previouslyKnownEnabled: false,
  userPhoneNumbersEnabled: false,
  personalProNounEnabled: false,
  housingEnabled: false,
  importType: null,
  l1Id: 0,
  l1Name: "placeholder",
  l2Id: 0,
  l2Name: "placeholder",
  l3: [],
  genderNetworkSettingsTransport: {
    genderSelectionCanHide: false,
    genderSelectionEnabled: false,
    genderSelectionVisibleToOthers: false,
    genderSelectionExplainer: "",
    genderSelectionTitle: "",
    genders: [],
    genderGroups: [],
  },
  l3CurrrentMatches: {},
  matchingText: {
    confirmMessage: "",
    confirmNotification: "",
  },
  matchingRoundStatus: "",
  passCode: 0,
  passCodeEnabled: false,
  petEnabled: false,
  reminderEmails: null,
  smokingEnabled: false,
  transgenderEnabled: false,
  transgenderOption: null,
  usersInNetwork: 0,
};

const generateExtendedL3 = (network: Network): IExtendedL3[] =>
  network.l3.map((l3) => ({
    ...l3,
    l2Id: network.l2Id,
    l2name: network.l2Name,
    matchingRoundStatus: network.matchingRoundStatus,
    key: `${network.l2Id}_${l3.l3Id}`,
    current: pathOr(0, ["l3CurrrentMatches", l3.l3Id], network),
  }));

const getUnitsTypes = compose(
  flatten,
  map<Network, IExtendedL3[]>(generateExtendedL3)
);

export const useNetwork = () => {
  const { getAuthToken, isSuperAdmin } = useAuth();
  const {
    selectedNetworkId,
    setSelectedNetworkId,
  } = useSelectedNetworkContext();
  const { networks, loadNetworkInfoForNetworks } = useNetworkContext();

  const selecteNetworkAsync = useCallback(async (networkId: number) => {
    if (!isSuperAdmin()) {
      const res = await loadNetworkInfoForNetworks([networkId], true);
      if (!res?.networks) {
        return;
      }
      setSelectedNetworkId(networkId);
      return;
    }

    const localNetwork = networks.find(propEq("l1Id", networkId));

    if (localNetwork) {
      setSelectedNetworkId(localNetwork.l1Id);
      return;
    }

    const res = await loadNetworkInfoForNetworks([networkId], true);

    if (!res?.networks) {
      return;
    }

    setSelectedNetworkId(networkId);
  }, []);

  const selectedNetwork = useMemo(
    () =>
      networks.find((network) => network?.l1Id === selectedNetworkId) ||
      DEFAULT_NETWORK,
    [selectedNetworkId, normalize(networks)]
  );

  const networkIds = useMemo(() => uniq(networks.map(prop("l1Id"))), [
    normalize(networks),
  ]);

  const filterNetworksByL1Id = useCallback(
    (l1Id: number) => networks.filter(propEq("l1Id", l1Id)),
    [normalize(networks)]
  );

  const getL2Options = useCallback(
    compose(uniqBy(prop("l2Id")), filterNetworksByL1Id),
    [filterNetworksByL1Id]
  );

  const getL2OptionsAsync = useCallback(
    async (l1Id: number) => {
      const response = await GetAllNetworkInfo([l1Id], await getAuthToken());

      if (response) {
        return response.networks.filter((x) => x.l1Id === l1Id);
      }

      return [];
    },
    [normalize(networks)]
  );

  const getL2 = useCallback(
    (l1Id: number, l2Id: number) => {
      if (!l1Id || !l2Id) {
        return null;
      }

      const network = networks.find((x) => x.l1Id === l1Id && x.l2Id === l2Id);

      if (!network) {
        return null;
      }

      return network;
    },
    [normalize(networks)]
  );

  const getL1 = useCallback(
    (l1Id: number) => {
      const ts = networks.find((x: Network) => x.l1Id === l1Id);
      return ts ? ts : null;
    },
    [normalize(networks)]
  );

  const getL2s = useCallback(
    (l1Id: number) => networks.filter((x: Network) => x.l1Id === l1Id),
    [normalize(networks)]
  );

  const getL3Options = useCallback(
    (l1Id: number, l2Id: number) => {
      const l2 = getL2(l1Id, l2Id);
      return l2 ? l2.l3 : [];
    },
    [getL2]
  );

  const getL3 = useCallback(
    (l1Id: number, l2Id: number, l3Id: number) => {
      if (!l1Id || !l2Id) {
        return undefined;
      }

      return getL3Options(l1Id, l2Id).find((x) => x.l3Id === l3Id);
    },
    [getL3Options]
  );

  const getL4 = useCallback(
    (l1Id: number, l2Id: number, l3Id: number, l4Id: number) => {
      const l3 = getL3(l1Id, l2Id, l3Id);

      if (!l3) {
        return undefined;
      }

      if (!l3.l4 || l3.l4.length == 0) {
        return undefined;
      }

      return l3.l4.find((l4) => l4.l4Id === l4Id);
    },
    [getL3]
  );

  const getCurrentMatchesByl2Id = useCallback(
    async (l2Id: number) =>
      Api.getCurrrentMatchesOfMatchesGroup(
        selectedNetworkId,
        l2Id,
        await getAuthToken()
      ),
    [selectedNetworkId]
  );

  const updateUnitTypesWithCurrentValue = useCallback(
    async (networks: Network[]) => {
      const promises = networks.map(
        compose(getCurrentMatchesByl2Id, prop("l2Id"))
      );
      const l3IdsCurrentValueMap = await Promise.all(promises);

      return networks.map((network, idx) => ({
        ...network,
        l3CurrrentMatches: l3IdsCurrentValueMap[idx].result,
      }));
    },
    [getCurrentMatchesByl2Id]
  );

  const checkSubnetworks = useCallback(
    compose(andThen(getUnitsTypes), updateUnitTypesWithCurrentValue),
    [updateUnitTypesWithCurrentValue, getUnitsTypes]
  );

  const checkSubnetworksWithoutCount = useCallback(
    compose(getUnitsTypes),
    [getUnitsTypes]
  );

  const getNetworkUnitTypes = useCallback(
    compose(checkSubnetworks, filterNetworksByL1Id),
    [checkSubnetworks, filterNetworksByL1Id]
  );

  const getNetworkUnitTypesWithoutCount = useCallback(
    compose(checkSubnetworksWithoutCount, filterNetworksByL1Id),
    [checkSubnetworksWithoutCount, filterNetworksByL1Id]
  );

  const getL4Options = useCallback(
    (l1Id: number, l2Id: number, l3Id: number) => {
      const l3 = getL3(l1Id, l2Id, l3Id);
      return l3 ? l3.l4 : [];
    },
    [getL3]
  );

  return {
    getL1,
    getL2,
    getL3,
    getL2s,
    getL4,
    getL4Options,
    getL3Options,
    networks,
    selecteNetworkAsync,
    networkIds,
    getL2Options,
    selectedNetwork,
    getL2OptionsAsync,
    getNetworkUnitTypes,
    getNetworkUnitTypesWithoutCount,
  };
};
